import type { RadioChangeEvent } from 'antd';
import { Radio as AntRadio } from 'antd';
import React from 'react';
import styled from 'styled-components';
import { theme } from '../../theme/semanticTokens';

export type { RadioChangeEvent };

export type SegmentedControlSize = 'small' | 'default';

export interface SegmentedControlOption {
  label: React.ReactNode;
  value: string | number;
  disabled?: boolean;
}

export interface SegmentedControlProps {
  options: SegmentedControlOption[];
  value?: string | number;
  defaultValue?: string | number;
  onChange?: (e: RadioChangeEvent) => void;
  size?: SegmentedControlSize;
  disabled?: boolean;
}

const StyledSegmentedControl = styled(AntRadio.Group)<{
  $size?: SegmentedControlSize;
}>`
  .ant-radio-button-wrapper {
    background: ${theme.dime.colors.surface.primary};
    border-color: ${theme.dime.colors.border.static};
    color: ${theme.dime.colors.text.primary};
    height: ${({ $size }) =>
      $size === 'small'
        ? theme.dime.spacing['2xl']
        : theme.dime.spacing['3xl']};
    line-height: ${({ $size }) =>
      $size === 'small'
        ? theme.dime.spacing['2xl']
        : theme.dime.spacing['3xl']};
    padding: 0 ${theme.dime.spacing.l};

    &:first-child {
      border-radius: ${theme.dime.radius.m} 0 0 ${theme.dime.radius.m};
    }

    &:last-child {
      border-radius: 0 ${theme.dime.radius.m} ${theme.dime.radius.m} 0;
    }

    &:not(.ant-radio-button-wrapper-checked):not(.ant-radio-button-wrapper-disabled):hover {
      background: ${theme.dime.colors.action.secondaryHover};
      color: ${theme.dime.colors.action.primaryHover};
    }
  }

  && .ant-radio-button-wrapper-checked {
    background: ${theme.dime.colors.action.primary};
    border-color: ${theme.dime.colors.border.interactiveSelected};
    color: ${theme.dime.colors.text.inverse};

    &:first-child {
      border-radius: ${theme.dime.radius.m} 0 0 ${theme.dime.radius.m};
    }

    &:last-child {
      border-radius: 0 ${theme.dime.radius.m} ${theme.dime.radius.m} 0;
    }

    &:not(.ant-radio-button-wrapper-disabled):hover {
      background: ${theme.dime.colors.action.primaryHover};
      border-color: ${theme.dime.colors.action.primaryHover};
      color: ${theme.dime.colors.text.inverse};
    }

    &::before {
      background-color: ${theme.dime.colors.border.interactiveSelected};
    }
  }

  && .ant-radio-button-wrapper-disabled {
    background: ${theme.dime.colors.fill.disabled};
    border-color: ${theme.dime.colors.border.interactiveDisabled};
    color: ${theme.dime.colors.text.disabled};
    cursor: not-allowed;

    &:hover {
      background: ${theme.dime.colors.fill.disabled};
      color: ${theme.dime.colors.text.disabled};
    }

    &.ant-radio-button-wrapper-checked {
      background: ${theme.dime.colors.action.primaryDisabled};
      border-color: ${theme.dime.colors.border.interactiveDisabled};
      color: ${theme.dime.colors.text.disabled};

      &::before {
        background-color: ${theme.dime.colors.border.interactiveDisabled};
      }
    }
  }
`;

export const SegmentedControl = React.forwardRef<
  HTMLDivElement,
  SegmentedControlProps
>(
  (
    { options, value, defaultValue, onChange, size = 'default', disabled },
    ref
  ) => (
    <StyledSegmentedControl
      ref={ref}
      optionType="button"
      buttonStyle="solid"
      {...(value !== undefined ? { value } : {})}
      defaultValue={defaultValue}
      onChange={onChange}
      disabled={disabled}
      $size={size}
    >
      {options.map((option) => (
        <AntRadio.Button
          key={String(option.value)}
          value={option.value}
          disabled={option.disabled}
        >
          {option.label}
        </AntRadio.Button>
      ))}
    </StyledSegmentedControl>
  )
);

SegmentedControl.displayName = 'SegmentedControl';
